/**
 * Phase 54.23.9 — Sidebar Favorites & Recent Pages.
 *
 * Pure client-side, localStorage-only. Zero backend, zero API,
 * zero permission changes. Every visible page is looked up by its
 * pathname against a small in-process PAGE_INDEX so we never
 * persist stale labels — if the app ever renames a route the
 * sidebar just picks up the new label on next load.
 */
import { useEffect, useState, useCallback } from "react";
import {
  LayoutDashboard, Building2, Users, ScrollText, Tag, ShoppingCart,
  Receipt, Truck, Banknote, StickyNote, Gem, Coins, Wallet, Watch,
  Database, Search, Package,
} from "lucide-react";

// ── Registry of every route that is legal to favorite / appear in
// recents. Kept in sync with UsmLayout NAV + HubTabs SECTIONS.
// Extending: add a row here and both features cover the new page
// automatically.
export const PAGE_INDEX = {
  "/usm":                    { label: "Panel",              icon: LayoutDashboard },
  "/usm/pos":                { label: "Ürün Satış",         icon: ShoppingCart },
  "/usm/price-lookup":       { label: "Fiyat Sorgu",        icon: Search },
  "/usm/sales":              { label: "Satışlar",           icon: Receipt },
  "/usm/transfers":          { label: "Transferler",         icon: Truck },
  "/usm/branches":           { label: "Şubeler",             icon: Building2 },
  "/usm/employees":          { label: "Çalışanlar",          icon: Users },
  "/usm/labels":             { label: "Etiket Şablonları",   icon: Tag },
  "/usm/inventory/silver":   { label: "Gümüş Envanteri",     icon: Gem },
  "/usm/inventory/gold":     { label: "Altın Envanteri",     icon: Coins },
  "/usm/inventory/watch":    { label: "Saat Envanteri",      icon: Watch },
  "/usm/accounting":         { label: "Muhasebe",            icon: Wallet },
  "/usm/notes":              { label: "Notlar",              icon: StickyNote },
  "/usm/audit":              { label: "Denetim",             icon: ScrollText },
  "/usm/system/backup":      { label: "Yedekleme",           icon: Database },
};

const FAV_KEY = "usm.sidebar.favorites";
const REC_KEY = "usm.sidebar.recents";
const MAX_FAV = 5;
// Phase 54.23.9 — store MAX_REC_STORED entries so the render layer
// can exclude the current pathname and still surface MAX_REC=3
// non-current recents. This is a defensive one-slot buffer.
const MAX_REC = 3;
const MAX_REC_STORED = MAX_REC + 1;

const readList = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((p) => typeof p === "string" && PAGE_INDEX[p]) : [];
  } catch {
    return [];
  }
};

const writeList = (key, list) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
    // Own-tab notification (storage event only fires cross-tab).
    window.dispatchEvent(new CustomEvent("usm-sidebar-prefs-change", { detail: { key } }));
  } catch {
    /* localStorage disabled — silently degrade */
  }
};

function useLocalPrefsList(key) {
  const [list, setList] = useState(() => readList(key));
  useEffect(() => {
    const sync = (e) => {
      if (!e || !e.detail || e.detail.key === key) setList(readList(key));
    };
    const storage = (e) => { if (e.key === key) setList(readList(key)); };
    window.addEventListener("usm-sidebar-prefs-change", sync);
    window.addEventListener("storage", storage);
    return () => {
      window.removeEventListener("usm-sidebar-prefs-change", sync);
      window.removeEventListener("storage", storage);
    };
  }, [key]);
  return list;
}

/** List of favorited pathnames (max 5). */
export function useFavorites() {
  return useLocalPrefsList(FAV_KEY);
}

/** List of recently visited pathnames (max 3, most-recent first). */
export function useRecents() {
  return useLocalPrefsList(REC_KEY);
}

/** Toggle a pathname's favorite state. Enforces the 5-item cap. */
export function toggleFavorite(pathname) {
  if (!PAGE_INDEX[pathname]) return { ok: false, reason: "unknown-page" };
  const cur = readList(FAV_KEY);
  const idx = cur.indexOf(pathname);
  if (idx >= 0) {
    const next = [...cur.slice(0, idx), ...cur.slice(idx + 1)];
    writeList(FAV_KEY, next);
    return { ok: true, added: false };
  }
  if (cur.length >= MAX_FAV) return { ok: false, reason: "cap-reached", cap: MAX_FAV };
  writeList(FAV_KEY, [...cur, pathname]);
  return { ok: true, added: true };
}

/** Push a pathname to the head of recents (most-recent-first). */
export function pushRecent(pathname) {
  if (!PAGE_INDEX[pathname]) return;
  const cur = readList(REC_KEY);
  const without = cur.filter((p) => p !== pathname);
  const next = [pathname, ...without].slice(0, MAX_REC_STORED);
  // Skip a write if nothing changed to avoid a needless event storm.
  if (JSON.stringify(next) === JSON.stringify(cur)) return;
  writeList(REC_KEY, next);
}

/**
 * Small hook that returns whether the current pathname is favorited
 * and a memoised toggle callback. Used by the star button in
 * PageHeader / BackupPage.
 */
export function useFavoriteToggle(pathname) {
  const favs = useFavorites();
  const isFav = favs.includes(pathname);
  const toggle = useCallback(() => toggleFavorite(pathname), [pathname]);
  return { isFav, toggle, isKnown: !!PAGE_INDEX[pathname] };
}
